/**
 * The bell's data layer.
 *
 * ## Not keyed by workspace or brand
 *
 * Every other feature in this console keys on `blogId` or the active org, because everything else
 * it shows belongs to one of them. A notification does not: it is addressed to a *person*, and
 * somebody who works across three workspaces has one bell. So these keys carry nothing but the
 * page, and switching workspace does not throw the feed away. (`resetQueryCache` still clears it
 * on sign-out, which is correct — that is a different person.)
 *
 * ## Polling, on purpose, and only the count
 *
 * There is no realtime transport in this codebase — no websocket, no SSE, nothing that pushes —
 * and inventing one for a badge would be a second connection to keep alive, authenticate, reconnect
 * and reason about, for a number that is allowed to be a minute stale. So the unread count is
 * polled on an interval and refetched when the window comes back to the front, which is the moment
 * a person actually looks at it.
 *
 * Only the *count* polls. The feed itself is fetched when the panel opens and invalidated on every
 * write — a list nobody is looking at is not worth a request a minute.
 */

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type {
  NotificationPreferences,
  NotificationUnreadCount,
  NotificationView,
  PartialNotificationPreferences,
} from '@anubis-connector/shared'

import { apiV1, apiV1Paged } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { qk } from '../../lib/query'

/**
 * How often the badge asks.
 *
 * Sixty seconds. Long enough that a tab left open all day costs a request a minute rather than a
 * request a second, short enough that "somebody assigned you a post" does not sit unseen for a
 * coffee break. The honest v1 of a feature that will one day be pushed.
 */
export const UNREAD_POLL_MS = 60_000

export interface NotificationPage {
  rows: NotificationView[]
  total: number
}

/* ── reading ───────────────────────────────────────────────────────────────── */

/** The badge. Polls, and refetches the moment the window is looked at again. */
export const useUnreadCount = (): UseQueryResult<NotificationUnreadCount> => {
  const { status } = useAuth()
  return useQuery({
    queryKey: qk.notificationsUnread(),
    enabled: status === 'authenticated',
    queryFn: () => apiV1<NotificationUnreadCount>('/notifications/unread-count'),
    refetchInterval: UNREAD_POLL_MS,
    /* The one query in the console that overrides the client's `refetchOnWindowFocus: false`.
       That default is right for analytics — a chart does not change while you are in another tab —
       and wrong here, because coming back to the tab is exactly when a stale badge is noticed. */
    refetchOnWindowFocus: true,
    staleTime: UNREAD_POLL_MS / 2,
  })
}

/** One page of the feed. Fetched when the panel opens, not on a timer. */
export const useNotifications = (page: number, enabled: boolean): UseQueryResult<NotificationPage> =>
  useQuery({
    queryKey: qk.notifications(page),
    enabled,
    staleTime: 15_000,
    queryFn: async () => {
      const result = await apiV1Paged<NotificationView>(`/notifications?page=${String(page)}`)
      return { rows: result.items, total: result.meta.total ?? result.items.length }
    },
  })

export const useNotificationPreferences = (): UseQueryResult<NotificationPreferences> =>
  useQuery({
    queryKey: qk.notificationPreferences(),
    staleTime: 5 * 60_000,
    queryFn: () => apiV1<NotificationPreferences>('/notifications/preferences'),
  })

/* ── writing ───────────────────────────────────────────────────────────────── */

/**
 * Every write invalidates the feed *and* the badge.
 *
 * They are two reads of the same rows, and a mark-read that refreshed the list without the number
 * would leave a badge counting something the panel no longer shows — the single most obvious way
 * for this feature to look broken.
 */
const useFeedInvalidation = (): (() => void) => {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['pd', 'notifications'] })
  }, [queryClient])
}

export const useMarkRead = (): UseMutationResult<NotificationView, unknown, string> => {
  const invalidate = useFeedInvalidation()
  return useMutation({
    mutationFn: (id: string) =>
      apiV1<NotificationView>(`/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export const useMarkAllRead = (): UseMutationResult<{ marked: number }, unknown, void> => {
  const invalidate = useFeedInvalidation()
  return useMutation({
    mutationFn: () => apiV1<{ marked: number }>('/notifications/read-all', { method: 'POST' }),
    onSuccess: invalidate,
  })
}

export const useUpdatePreferences = (): UseMutationResult<
  NotificationPreferences,
  unknown,
  PartialNotificationPreferences
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: PartialNotificationPreferences) =>
      apiV1<NotificationPreferences>('/notifications/preferences', { method: 'PUT', json: patch }),
    /* The server answers with the whole resolved matrix, so the cache takes it verbatim rather
       than refetching what we are already holding. */
    onSuccess: (next) => {
      queryClient.setQueryData(qk.notificationPreferences(), next)
    },
  })
}
